/**
 * AdminHelpModal.jsx
 * 
 * Modal that displays help documentation for each admin menu section.
 * Shows title, description, features, and tips for the selected section.
 */

import { useState, useEffect } from "react";
import { X, HelpCircle, Lightbulb, CheckCircle, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { apiClient } from "@/lib/api";

export default function AdminHelpModal({ isOpen, onClose, section, adminToken }) {
  const [helpContent, setHelpContent] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && section) {
      fetchHelp();
    }
  }, [isOpen, section]);

  const fetchHelp = async () => {
    setLoading(true);
    try {
      const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
      const res = await apiClient.get(`/admin/menu-help/${section}`, { headers });
      setHelpContent(res.data.help);
    } catch (err) {
      console.error("Failed to fetch help:", err);
      setHelpContent(null);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-viraly-card rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-2xl border border-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-white/10 bg-gradient-to-r from-viraly-orange/20 to-transparent">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-viraly-orange/20 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-viraly-orange" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-viraly-light">
                    {loading ? "Loading..." : helpContent?.title || "Help"}
                  </h2>
                  <p className="text-sm text-viraly-muted">Admin Guide</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-viraly-muted" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-8 h-8 border-2 border-viraly-orange border-t-transparent rounded-full" />
              </div>
            ) : helpContent ? (
              <div className="space-y-6">
                {/* Description */}
                <div className="p-4 rounded-xl bg-white/5">
                  <p className="text-viraly-light">{helpContent.description}</p>
                </div>

                {/* Features */}
                {helpContent.features && helpContent.features.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-viraly-orange mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Features
                    </h3>
                    <div className="space-y-2">
                      {helpContent.features.map((feature, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                        >
                          <div className="w-5 h-5 rounded-full bg-viraly-success/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <CheckCircle className="w-3 h-3 text-viraly-success" />
                          </div>
                          <span className="text-sm text-viraly-light">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tips */}
                {helpContent.tips && helpContent.tips.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Pro Tips
                    </h3>
                    <div className="space-y-2">
                      {helpContent.tips.map((tip, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20"
                        >
                          <Lightbulb className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-amber-200">{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <HelpCircle className="w-12 h-12 text-viraly-muted mx-auto mb-3" />
                <p className="text-viraly-muted">No help available for this section.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/10 bg-white/5">
            <Button onClick={onClose} className="w-full btn-primary">
              Got it!
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
